import { FC, useState, useEffect } from "react";
import WrapperFilterContainer from "../../atomos/WrapperFilterContainer";
import {
  MenuItem,
  styled,
  TextField as TextFieldMui,
  Button,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BasicRangeShortcuts from "../DataRangePicker";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import dayjs, { Dayjs } from "dayjs";
import ClearIcon from "@mui/icons-material/Clear";

const StyledTextField = styled(TextFieldMui)(({ theme }) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    height: 57,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      height: 48,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16.5px 14px",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 10px !important",
      fontSize: "0.875rem",
    },
  },
  "& .MuiInputLabel-root": {
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.875rem",
    },
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#ccc" },
    "&:hover fieldset": { borderColor: "#888" },
    "&.Mui-focused fieldset": { borderColor: "#1976d2" },
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  height: 57,
  borderRadius: 8,
  minWidth: 120,
  fontSize: "0.85rem",
  [theme.breakpoints.down("sm")]: {
    height: 48,
    minWidth: "100%",
    fontSize: "0.75rem",
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.5),
    },
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.85rem",
  },
}));

interface IBucketFilterControllerProps {
  search: string;
  setSearch: (val: string) => void;
  project_state: string;
  setProjectState: (val: string) => void;
  from: string;
  to: string;
  onHandleDateRange: (val: any) => void;
  onClearAllFilters: () => void;
}

export const BucketFilterController: FC<IBucketFilterControllerProps> = ({
  search,
  setSearch,
  project_state,
  setProjectState,
  from,
  to,
  onHandleDateRange,
  onClearAllFilters,
}) => {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  // Acceso directo a dispatch y al estado paginado

  // Actualizar estado local de fechas
  useEffect(() => {
    setDateRange([from ? dayjs(from) : null, to ? dayjs(to) : null]);
  }, [from, to]);



  const handleClearAll = () => {
    setDateRange([null, null]);
    onClearAllFilters();
  };

  return (
    <WrapperFilterContainer
      sx={{
        padding: { xs: 1, sm: 1.5, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box>
        {/* Primera fila: Búsqueda y Estado */}
        <Grid
          container
          spacing={{ xs: 1, sm: 1.5, md: 2 }}
          sx={{ mb: { xs: 1, sm: 1.5, md: 2 } }}
        >
          {/* Campo de Búsqueda */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StyledTextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label="Buscar"
              variant="outlined"
              fullWidth
            />
          </Grid>

          {/* Selector de Estado */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StyledTextField
              select
              label="Estado Proyecto"
              value={project_state}
              onChange={(e) => setProjectState(e.target.value)}
              fullWidth
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="En proceso">En proceso</MenuItem>
              <MenuItem value="En Curso">En Curso</MenuItem>
              <MenuItem value="Finalizado">Finalizado</MenuItem>
            </StyledTextField>
          </Grid>

          {/* Fecha y Botón para pantallas grandes */}
          {isLargeScreen && (
            <>
              <Grid item md={4} lg={4}>
                <Box
                  sx={{
                    width: "60rem",
                    "& .MuiFormControl-root": { width: "100%" },
                    "& .MuiOutlinedInput-root": {
                      height: 57,
                      borderRadius: 2,
                    },
                  }}
                >
                  <BasicRangeShortcuts
                    dateRange={dateRange}
                    onChange={onHandleDateRange}
                  />
                </Box>
              </Grid>
              <Grid item md={2} lg={2}>
                <ClearButton
                  variant="contained"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAll}
                  disabled={
                    !search && !project_state && !dateRange[0] && !dateRange[1]
                  }
                  fullWidth
                >
                  Limpiar
                </ClearButton>
              </Grid>
            </>
          )}
        </Grid>

        {/* Segunda fila para móviles y tablets */}
        {!isLargeScreen && (
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
            <Grid item xs={12} sm={8} md={9}>
              <Box
                sx={{
                  width: "222%",
                  "& .MuiFormControl-root": { width: "19rem", padding: 0 },
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 48, sm: 57 },
                    borderRadius: 2,
                  },
                  "& .MuiTextField-root": {
                    flexDirection: { xs: "column", sm: "row" },
                  },
                }}
              >
                <BasicRangeShortcuts
                  dateRange={dateRange}
                  onChange={onHandleDateRange}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <ClearButton
                variant="contained"
                color="error"
                startIcon={!isMobile && <ClearIcon />}
                onClick={handleClearAll}
                disabled={
                  !search && !project_state && !dateRange[0] && !dateRange[1]
                }
                fullWidth
              >
                {isMobile ? "Limpiar" : "Limpiar filtros"}
              </ClearButton>
            </Grid>
          </Grid>
        )}
      </Box>
    </WrapperFilterContainer>
  );
};

export default BucketFilterController;
